import React, { Suspense } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import type { RouteComponentProps } from 'react-router';
import { useSelector } from 'react-redux';
import SecuredApp from '~/components/containers/SecuredAppContainer/SecuredAppContainer';
import SuspendedRoute from '~/components/views/SuspendedRoute/SuspendedRoute';
import TrackingRedirect from '~/components/views/tracking/TrackingRedirect/TrackingRedirect';
import { getMappedRoutes } from '~/app/routes/routes';
import paths from '~/app/paths/paths';
import type { AppState } from '~/reducers';
import { useWebviewRouter } from '~/domain/native/hooks/use-webview-router';
import { useAssetListUpdate } from '~/domain/native/hooks/use-asset-list-update';
import Loading from '~/components/views/common/Loading/Loading';
import SplashScreen from '~/components/views/SplashScreen/SplashScreen';
import WebViewReload from '~/domain/native/components/WebViewReload';
import { hasWebViewHost } from '~/domain/native/utils/has-webview-host';

const getRouteComponents = () => {
  const mappedRoutes = getMappedRoutes();
  const flattenedRoutes = mappedRoutes.redirectRoutes
    .map((route) => (
      <Route
        exact
        key={route.path as string}
        path={route.path}
        render={(props: RouteComponentProps) => {
          return (
            <Redirect to={route.redirect(props.match.params, props.location.search)} push={false} />
          );
        }}
      />
    ))
    .concat(
      mappedRoutes.publicRoutes.map((route) => (
        <SuspendedRoute
          exact
          key={route.path as string}
          path={route.path}
          render={(props) => <route.Component {...props} />}
        />
      ))
    );

  const allSecureRoutePaths = mappedRoutes.securedRoutes.reduce<string[]>((acc, curr) => {
    if (Array.isArray(curr.path)) {
      acc = acc.concat(curr.path);
    } else {
      acc.push(curr.path);
    }
    return acc;
  }, []);

  flattenedRoutes.push(
    <SuspendedRoute
      exact
      key="SECURED_PATHS"
      path={allSecureRoutePaths}
      render={(props) => <SecuredApp {...props} />}
    />
  );
  return flattenedRoutes;
};

const WebViewRoutes = () => {
  useWebviewRouter();
  useAssetListUpdate();
  const mappedRoutes = getMappedRoutes();
  return (
    <Switch>
      <SuspendedRoute
        exact
        key="SECURED_PATHS"
        path={mappedRoutes.securedWebviewRoutes.reduce<string[]>((acc, curr) => {
          if (Array.isArray(curr.path)) {
            acc = acc.concat(curr.path);
          } else {
            acc.push(curr.path);
          }
          return acc;
        }, [])}
        render={(props) => <SecuredApp {...props} />}
      />
      <Route
        render={() => (
          <Suspense fallback={<Loading />}>
            <WebViewReload />
          </Suspense>
        )}
      />
    </Switch>
  );
};

export interface Props {
  landingPage: string;
}

const AppSwitch = ({ landingPage }: Props) => {
  const onWebView = useSelector((state: AppState) => state.native.onWebView);
  // Inside a WebView host: wait for onWebView to resolve — avoids the
  // transient / → /login redirect that older fleet-app builds treat as
  // SessionExpired.
  if (onWebView === undefined && hasWebViewHost()) {
    return <SplashScreen />;
  }
  return onWebView ? (
    <WebViewRoutes />
  ) : (
    <Switch>
      <Route exact path="/" render={() => <Redirect to={paths.login.getPath()} />} />
      {getRouteComponents()}
      <Route
        key="REDIRECT"
        render={() => <TrackingRedirect to={landingPage} tracking={{ label: 'Route not found' }} />}
      />
    </Switch>
  );
};

export default AppSwitch;
